// src/components/calendar/EventCreationPanel/EventCreationPanel.jsx - Migrated to shadcn (Grid Layout Version)
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AlertTriangle, Calendar, Users, Handshake, Settings, X, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { getIANAOffset } from '../../../utils/timezone';

// shadcn components
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Separator } from '../../ui/separator';

// Custom components that are still needed
import { PartnerServiceDetailsCard } from '../../ui/cards/PartnerServiceDetailsCard';

// Hooks
import { useCustomers } from '../../../hooks/useCustomers';
import { useSettings } from '../../../hooks/useSettings';
import { usePartners } from '../../../hooks/usePartners';
import { useEvents } from '../../../hooks/useEvents';

const EventCreationPanel = ({
  isOpen,
  onClose,
  selectedDateTime,
  onDateTimeChange,
  onPreviewUpdate,
  currentCalendarDateRange,
  onPartnerAvailabilityRequest,
  onClearPartnerAvailability,
  preSelectedCustomer,
}) => {
  const instanceId = useRef(`${Math.random().toString(36).substring(2, 9)}`);
  const renderCount = useRef(0);

  console.log(`🔍 [${instanceId.current}] EventCreationPanel RENDER #${++renderCount.current}`, {
    isOpen,
    selectedDateTime
  });

  // ===============================================
  // HOOKS
  // ===============================================

  const {
    customers,
    loadCustomers,
    loading: loadingCustomers,
    error: customersError
  } = useCustomers();

  const {
    services,
    businessHours,
    loadServices,
    getBusinessHoursForDay,
    getSetting,
    loading: loadingServices,
    error: servicesError
  } = useSettings();

  const {
    connectedPartners,
    loadPartnerNetwork,
    partnerServices,
    servicesLoading,
    loadPartnerServices,
    loading: loadingPartners,
    error: partnersError
  } = usePartners();

  const { createEvent, creatingEvent } = useEvents();

  // ===============================================
  // FORM STATE MANAGEMENT
  // ===============================================

  const getInitialFormState = useCallback(() => ({
    eventType: 'general',
    title: '',
    eventDateTime: selectedDateTime,
    duration: 180,
    description: '',
    serviceType: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerId: '',
    serviceAddress: '',
    serviceNotes: '',
    partnerId: '',
    partnerServiceId: '',
    partnerName: undefined,
    serviceId: '',
    serviceName: undefined,
    partner: null,
    partnerService: null,
    isBlocking: true,
    blockingType: 'capacity'
  }), [selectedDateTime]);

  const [formData, setFormData] = useState(getInitialFormState());
  const [error, setError] = useState(null);

  // Combobox open states
  const [customerComboOpen, setCustomerComboOpen] = useState(false);
  const [serviceComboOpen, setServiceComboOpen] = useState(false);
  const [partnerComboOpen, setPartnerComboOpen] = useState(false);

  // ===============================================
  // CANONICAL: creatingEvent flag watcher — close panel on server confirmation
  // ===============================================

  const prevCreatingEvent = useRef(false);
  useEffect(() => {
    if (prevCreatingEvent.current && !creatingEvent) {
      // Server confirmed (success or error) — close panel
      handleClose();
    }
    prevCreatingEvent.current = creatingEvent;
  }, [creatingEvent]);

  // ===============================================
  // DATA LOADING EFFECTS
  // ===============================================

  useEffect(() => {
    if (isOpen) {
      console.log(`🔍 [${instanceId.current}] Panel opened, loading data`);

      if (customers.length === 0) {
        loadCustomers();
      }

      if (services.length === 0) {
        loadServices();
      }

      if (connectedPartners.length === 0) {
        loadPartnerNetwork();
      }
    }
  }, [isOpen, customers.length, services.length, connectedPartners.length]);

  useEffect(() => {
    if (formData.partnerId && formData.eventType === 'partner_service') {
      console.log('🔧 Loading services for selected partner:', formData.partnerId);
      loadPartnerServices(formData.partnerId);
    }
  }, [formData.partnerId, formData.eventType, loadPartnerServices]);

  // ===============================================
  // FORM STATE UPDATES
  // ===============================================

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      eventDateTime: selectedDateTime
    }));
  }, [selectedDateTime]);

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormState());
      setError(null);
    }
  }, [isOpen, getInitialFormState]);

  useEffect(() => {
    if (isOpen && preSelectedCustomer) {
      console.log('🎯 EventCreationPanel: Pre-selected customer detected:', preSelectedCustomer);

      setFormData(prev => ({
        ...prev,
        eventType: 'service',
        customerId: preSelectedCustomer.id,
        customerName: preSelectedCustomer.name || '',
        customerEmail: preSelectedCustomer.email || '',
        customerPhone: preSelectedCustomer.phone || ''
      }));

      if (onPreviewUpdate) {
        setTimeout(() => {
          onPreviewUpdate({
            eventType: 'service',
            customerId: preSelectedCustomer.id,
            customerName: preSelectedCustomer.name || '',
            customerEmail: preSelectedCustomer.email || '',
            customerPhone: preSelectedCustomer.phone || ''
          });
        }, 100);
      }
    }
  }, [isOpen, preSelectedCustomer, onPreviewUpdate]);

  // ===============================================
  // FORM HANDLERS
  // ===============================================

  const handleInputChange = useCallback((field, value) => {
    console.log(`🔍 [${instanceId.current}] Field change:`, field, '=', value);

    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      if (error) {
        setError(null);
      }

      if (onPreviewUpdate) {
        setTimeout(() => {
          onPreviewUpdate(newData);
        }, 100);
      }

      return newData;
    });
  }, [error, onPreviewUpdate]);

  // ===============================================
  // PARTNER AVAILABILITY HANDLER
  // ===============================================

  const handlePartnerChange = useCallback((partnerId) => {
    const partner = connectedPartners.find(p => p.id === partnerId);

    handleInputChange('partnerId', partnerId);

    if (partner && onPartnerAvailabilityRequest && currentCalendarDateRange) {
      console.log('📅 EventCreationPanel: Requesting availability for partner:', partner.business_name);
      onPartnerAvailabilityRequest(partner, currentCalendarDateRange);
    }
  }, [connectedPartners, handleInputChange, onPartnerAvailabilityRequest, currentCalendarDateRange]);

  const getPartnerServices = useCallback(() => {
    if (!formData.partnerId || !partnerServices[formData.partnerId]) {
      return [];
    }

    return partnerServices[formData.partnerId].map(service => ({
      value: service.id,
      label: service.name,
      description: service.description,
      duration: service.duration_minutes,
      price: service.price
    }));
  }, [formData.partnerId, partnerServices]);

  const handlePartnerServiceChange = useCallback((serviceId) => {
    const selectedService = getPartnerServices().find(s => s.value === serviceId);

    handleInputChange('partnerServiceId', serviceId);

    if (selectedService && selectedService.duration) {
      handleInputChange('duration', selectedService.duration);
    }
  }, [getPartnerServices, handleInputChange]);

  const handleClose = useCallback(() => {
    console.log(`🔍 [${instanceId.current}] PANEL CLOSING`);

    setFormData(getInitialFormState());
    setError(null);

    if (onClose) {
      onClose();
    }
  }, [onClose, getInitialFormState]);

  // ===============================================
  // CUSTOMER MANAGEMENT HANDLERS
  // ===============================================

  const handleCustomerSelect = useCallback((customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      handleInputChange('customerId', customer.id);
      handleInputChange('customerName', customer.name);
      handleInputChange('customerEmail', customer.email);
      handleInputChange('customerPhone', customer.phone);
    }
    setCustomerComboOpen(false);
  }, [customers, handleInputChange]);

  const handleClearCustomerSelection = useCallback(() => {
    handleInputChange('customerId', '');
  }, [handleInputChange]);

  const handleServiceSelect = useCallback((serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      handleInputChange('serviceType', service.name);
      handleInputChange('serviceId', service.id);
      if (service.duration_minutes) {
        handleInputChange('duration', service.duration_minutes);
      }
    }
    setServiceComboOpen(false);
  }, [services, handleInputChange]);

  // ===============================================
  // FORM SUBMISSION
  // ===============================================

  const handleSubmit = useCallback(() => {
    console.log(`🔍 [${instanceId.current}] FORM SUBMITTED:`, formData);

    const scheduledDateTime = formData.eventDateTime;

    console.log('🌍 Using ISO datetime:', scheduledDateTime);

    let eventData;

    if (formData.eventType === 'partner_service') {
      eventData = {
        type: 'partner_service',
        event_category: 'service',
        title: formData.title,
        scheduled_datetime: scheduledDateTime,
        duration_minutes: formData.duration,
        external_business_id: formData.partnerId,
        requesting_business_name: 'Business Name',
        service_type: formData.partnerServiceId,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        service_address: formData.serviceAddress
      };
    } else {
      eventData = {
        title: formData.title,
        scheduled_datetime: scheduledDateTime,
        duration_minutes: formData.duration,
        description: formData.description,
        event_category: formData.eventType,
        status: 'pending',
        is_blocking: formData.isBlocking,
        blocking_type: formData.blockingType,

        ...(formData.eventType === 'service' && {
          ...(formData.customerId && { customer_id: formData.customerId }),
          customer_name: formData.customerName,
          customer_email: formData.customerEmail,
          customer_phone: formData.customerPhone,
          service_address: formData.serviceAddress,
          service_notes: formData.serviceNotes,
          service_type: formData.serviceType
        })
      };
    }

    createEvent(eventData);
    // Panel close handled by creatingEvent flag watcher

  }, [formData, createEvent]);

  // ===============================================
  // RENDER OPTIONS
  // ===============================================

  const eventTypeOptions = [
    {
      value: 'general',
      label: 'General Event',
      description: 'Block time for meetings, breaks, or other activities'
    },
    {
      value: 'service',
      label: 'Customer Appointment',
      description: 'Schedule a service appointment with a customer'
    }
    // {
    //   value: 'partner_service',
    //   label: 'Partner Service Booking',
    //   description: 'Book a service with a partner business'
    // }
  ];

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
    { value: '240', label: '4 hours' },
    { value: '480', label: '8 hours (Full day)' }
  ];

  const customerOptions = useMemo(() => {
    return customers.map(customer => ({
      value: customer.id,
      label: customer.name,
      email: customer.email,
      phone: customer.phone,
      searchText: `${customer.name} ${customer.email} ${customer.phone || ''}`
    }));
  }, [customers]);

  const serviceTypeOptions = useMemo(() => {
    return services.map(service => ({
      value: service.id,
      label: service.name,
      description: service.description,
      duration: service.duration_minutes,
      price: service.price
    }));
  }, [services]);

  const partnerOptions = useMemo(() => {
    return connectedPartners.map(partner => ({
      value: partner.id,
      label: partner.business_name || partner.name
    }));
  }, [connectedPartners]);

  const scheduleWarnings = useMemo(() => {
    if (formData.eventType !== 'service' || !formData.eventDateTime) return null;
    const warnings = [];
    const selected = new Date(formData.eventDateTime);

    // Business hours — JS getDay() matches backend day_of_week (0=Sun…6=Sat)
    const dayHours = getBusinessHoursForDay(selected.getDay());
    if (!dayHours) {
      warnings.push('Business is closed on this day');
    } else {
      const selMins = selected.getHours() * 60 + selected.getMinutes();
      const [oH, oM] = dayHours.open.split(':').map(Number);
      const [cH, cM] = dayHours.close.split(':').map(Number);
      if (selMins < oH * 60 + oM || selMins >= cH * 60 + cM) {
        warnings.push(`Outside business hours (${dayHours.open} – ${dayHours.close})`);
      }
    }

    // Advance notice
    const minNotice = getSetting('min_notice_hours', 3);
    if (minNotice > 0 && selected <= new Date(Date.now() + minNotice * 3600 * 1000)) {
      warnings.push(`Within the ${minNotice}-hour advance notice window`);
    }

    return warnings.length > 0 ? warnings : null;
  }, [formData.eventDateTime, formData.eventType, getBusinessHoursForDay, getSetting]);

  // ===============================================
  // RENDER HELPERS
  // ===============================================

  const isLoadingData = loadingCustomers || loadingServices || loadingPartners;
  const hasDataErrors = customersError || servicesError || partnersError;

  // ===============================================
  // RENDER
  // ===============================================

  return (
    <div className="h-full flex flex-col bg-slate-900/95 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl rounded-r-2xl overflow-hidden">
      {/* Panel Header */}
      <div className="px-6 py-3 border-b border-slate-700/50">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-200">
              {formData.eventType === 'partner_service' ?
                'Book Partner Service' :
                formData.eventType === 'service' ?
                'New Customer Appointment' :
                'New Event'
              }
            </h2>
            <p className="text-sm text-slate-400 mt-0.5">
              {formData.eventDateTime ?
                new Date(formData.eventDateTime).toLocaleString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }) : ''
              }
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 scrollbar-custom">
        <form className="space-y-6 pb-6">
          {/* Data Loading Indicator */}
          {isLoadingData && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-blue-300 text-sm">Loading form data...</span>
              </div>
            </div>
          )}

          {/* Data Error Indicator */}
          {hasDataErrors && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
              <div className="text-yellow-300 text-sm">
                Some form data may not be available. You can still create basic events.
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Event Type Selection */}
          <div className="space-y-3">
            <h3 className="text-base font-semibold flex items-center gap-2 text-foreground">
              <Calendar className="w-4 h-4" />
              Event Type
            </h3>

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="eventType" className="text-sm font-medium">Type of Event</Label>
                <Select
                  value={formData.eventType}
                  onValueChange={(value) => handleInputChange('eventType', value)}
                >
                  <SelectTrigger id="eventType">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {eventTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-xs text-muted-foreground">{option.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Basic Event Details */}
          <div className="space-y-3 border-t border-border pt-6">
            <h3 className="text-base font-semibold text-foreground">Event Details</h3>

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">Event Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter event title..."
                />
              </div>

              {/* Date/Time + Duration Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="datetime" className="text-sm font-medium">Event Date & Time</Label>
                  <Input
                    id="datetime"
                    type="datetime-local"
                    value={formData.eventDateTime ?
                      (() => {
                        const date = new Date(formData.eventDateTime);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}`;
                      })() : ''
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        const bizTimezone = getSetting('timezone', 'America/Chicago');
                        const offset = getIANAOffset(e.target.value, bizTimezone);
                        const isoDateTime = `${e.target.value}:00${offset}`;
                        handleInputChange('eventDateTime', isoDateTime);
                        if (onDateTimeChange) {
                          onDateTimeChange(isoDateTime);
                        }
                      }
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-sm font-medium">Duration</Label>
                  <Select
                    value={String(formData.duration)}
                    onValueChange={(value) => handleInputChange('duration', parseInt(value))}
                  >
                    <SelectTrigger id="duration">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {durationOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Optional event description..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Blocking Configuration - ONLY for general events */}
          {formData.eventType === 'general' && (
            <div className="space-y-3 border-t border-border pt-6">
              <h3 className="text-base font-semibold flex items-center gap-2 text-foreground">
                <Settings className="w-4 h-4" />
                Scheduling Options
              </h3>

              <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Blocks Scheduling</Label>
                      <p className="text-sm text-muted-foreground">
                        Prevents other appointments from being scheduled at this time
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant={formData.isBlocking ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleInputChange('isBlocking', !formData.isBlocking)}
                    >
                      {formData.isBlocking ? 'Yes' : 'No'}
                    </Button>
                  </div>

                  {formData.isBlocking && (
                    <div className="space-y-2">
                      <Label htmlFor="blockingType" className="text-sm font-medium">Blocking Type</Label>
                      <Select
                        value={formData.blockingType}
                        onValueChange={(value) => handleInputChange('blockingType', value)}
                      >
                        <SelectTrigger id="blockingType">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="capacity">
                              <div>
                                <div className="font-medium">Capacity Block</div>
                                <div className="text-xs text-muted-foreground">Reduces available appointment slots</div>
                              </div>
                            </SelectItem>
                            <SelectItem value="full">
                              <div>
                                <div className="font-medium">Full Block</div>
                                <div className="text-xs text-muted-foreground">Completely blocks the time slot</div>
                              </div>
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Service Event Fields */}
          {(formData.eventType === 'service' || formData.eventType === 'partner_service') && (
            <div className="space-y-3 border-t border-border pt-6">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Users className="w-4 h-4" />
                Customer Information
              </h3>

                {formData.eventType === 'service' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Service Type</Label>
                    {serviceTypeOptions.length > 0 ? (
                      <Popover open={serviceComboOpen} onOpenChange={setServiceComboOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={serviceComboOpen}
                            className="w-full justify-between"
                          >
                            {formData.serviceId
                              ? serviceTypeOptions.find((s) => s.value === formData.serviceId)?.label
                              : "Select service type..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search service..." />
                            <CommandList>
                              <CommandEmpty>No service found.</CommandEmpty>
                              <CommandGroup>
                                {serviceTypeOptions.map((service) => (
                                  <CommandItem
                                    key={service.value}
                                    value={service.label}
                                    onSelect={() => handleServiceSelect(service.value)}
                                  >
                                    <div className="flex-1">
                                      <div className="font-medium">{service.label}</div>
                                      {service.description && (
                                        <div className="text-sm text-muted-foreground">{service.description}</div>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end text-xs text-muted-foreground ml-2">
                                      {service.duration && <span>{service.duration} min</span>}
                                      {service.price && <span className="text-emerald-400">${service.price}</span>}
                                    </div>
                                    <Check
                                      className={cn(
                                        "ml-2 h-4 w-4",
                                        formData.serviceId === service.value ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Input
                        value={formData.serviceType}
                        onChange={(e) => handleInputChange('serviceType', e.target.value)}
                        placeholder="Enter service type manually..."
                      />
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Customer</Label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Popover open={customerComboOpen} onOpenChange={setCustomerComboOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={customerComboOpen}
                            className="w-full justify-between"
                          >
                            {formData.customerId
                              ? customerOptions.find((c) => c.value === formData.customerId)?.label
                              : "Search and select customer or enter details manually..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search customers..." />
                            <CommandList>
                              <CommandEmpty>No customer found.</CommandEmpty>
                              <CommandGroup>
                                {customerOptions.map((customer) => (
                                  <CommandItem
                                    key={customer.value}
                                    value={customer.searchText}
                                    onSelect={() => handleCustomerSelect(customer.value)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-semibold text-white text-xs">
                                        {customer.label.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium">{customer.label}</div>
                                        <div className="text-sm text-muted-foreground">{customer.email}</div>
                                        {customer.phone && <div className="text-sm text-muted-foreground">{customer.phone}</div>}
                                      </div>
                                    </div>
                                    <Check
                                      className={cn(
                                        "ml-2 h-4 w-4",
                                        formData.customerId === customer.value ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    {formData.customerId && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleClearCustomerSelection}
                        title="Clear selection to enter customer details manually"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Customer Details - Always Editable */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customerName" className="text-sm font-medium">Customer Name</Label>
                    <Input
                      id="customerName"
                      value={formData.customerName}
                      onChange={(e) => handleInputChange('customerName', e.target.value)}
                      placeholder="Customer name..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerEmail" className="text-sm font-medium">Customer Email</Label>
                    <Input
                      id="customerEmail"
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                      placeholder="customer@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customerPhone" className="text-sm font-medium">Customer Phone</Label>
                    <Input
                      id="customerPhone"
                      value={formData.customerPhone}
                      onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serviceAddress" className="text-sm font-medium">Service Address</Label>
                    <Textarea
                      id="serviceAddress"
                      value={formData.serviceAddress}
                      onChange={(e) => handleInputChange('serviceAddress', e.target.value)}
                      placeholder="Full address where service will be performed..."
                      rows={3}
                    />
                  </div>
                </div>

                <div className="text-xs text-slate-400 bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
                  💡 <strong>Tip:</strong> Select an existing customer from the dropdown above, or fill in the details manually to create a new customer automatically.
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serviceNotes" className="text-sm font-medium">Service Notes</Label>
                  <Textarea
                    id="serviceNotes"
                    value={formData.serviceNotes}
                    onChange={(e) => handleInputChange('serviceNotes', e.target.value)}
                    placeholder="Additional notes about the service..."
                    rows={3}
                  />
                </div>
            </div>
          )}

          {/* Partner Service Fields */}
          {/* {formData.eventType === 'partner_service' && (
            <div className="space-y-3 border-t border-border pt-6">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Handshake className="w-4 h-4" />
                Partner Information
              </h3>

              {/* Step 1: Select Partner */}
              {/* <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                      <span className="text-blue-400 text-sm font-semibold">1</span>
                    </div>
                    <h4 className="text-sm font-semibold text-slate-300">Select Partner</h4>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Partner Business</Label>
                    <Popover open={partnerComboOpen} onOpenChange={setPartnerComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={partnerComboOpen}
                          disabled={loadingPartners}
                          className="w-full justify-between"
                        >
                          {formData.partnerId
                            ? partnerOptions.find((p) => p.value === formData.partnerId)?.label
                            : "Select partner business..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search partners..." />
                          <CommandList>
                            <CommandEmpty>No partner found.</CommandEmpty>
                            <CommandGroup>
                              {partnerOptions.map((partner) => (
                                <CommandItem
                                  key={partner.value}
                                  value={partner.label}
                                  onSelect={() => {
                                    handlePartnerChange(partner.value);
                                    setPartnerComboOpen(false);
                                  }}
                                >
                                  {partner.label}
                                  <Check
                                    className={cn(
                                      "ml-auto h-4 w-4",
                                      formData.partnerId === partner.value ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div> */}

                {/* Step 2: Select Service */}
                {/* {formData.partnerId && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                        <span className="text-purple-400 text-sm font-semibold">2</span>
                      </div>
                      <h4 className="text-sm font-semibold text-slate-300">Select Service</h4>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Partner Service</Label>
                      <Select
                        value={formData.partnerServiceId}
                        onValueChange={handlePartnerServiceChange}
                        disabled={servicesLoading || getPartnerServices().length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={servicesLoading ? "Loading services..." : "Select service..."} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {getPartnerServices().map((service) => (
                              <SelectItem key={service.value} value={service.value}>
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium">{service.label}</div>
                                    {service.description && (
                                      <div className="text-sm text-muted-foreground">{service.description}</div>
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end text-xs text-muted-foreground ml-2">
                                    {service.duration && <span>{service.duration} min</span>}
                                    {service.price && <span className="text-emerald-400">${service.price}</span>}
                                  </div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div> */}

                    {/* Show service details when selected */}
                    {/* {formData.partnerServiceId && (() => {
                      const selectedService = getPartnerServices().find(s => s.value === formData.partnerServiceId);
                      return selectedService && (
                        <div className="pl-8">
                          <PartnerServiceDetailsCard service={selectedService} />
                        </div>
                      );
                    })()} */}

                    {/* {formData.partnerId && !servicesLoading && getPartnerServices().length === 0 && (
                      <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 ml-8">
                        This partner has no available services for booking.
                      </div>
                    )}
                  </div>
                )} */}
            {/* </div>
          )} */}
        </form>
      </div>

      {/* Schedule Warnings — admin override notice for service events */}
      {scheduleWarnings && (
        <div className="px-4 pb-3">
          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-300 space-y-0.5">
              <p className="font-medium text-amber-200">Scheduling notice</p>
              {scheduleWarnings.map((w, i) => <p key={i}>{w}</p>)}
              <p className="text-amber-400/70 mt-1">Admin scheduling bypasses these restrictions — you can still save.</p>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Footer */}
      <div className="border-t border-slate-700/50 p-4 bg-slate-900/50 backdrop-blur-sm">
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={creatingEvent}
            className="flex-1"
          >
            Cancel
          </Button>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={creatingEvent || !formData.title}
            className="flex-1"
          >
            {creatingEvent ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Creating...
              </>
            ) : (
              'Create Event'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventCreationPanel;
